import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { getApiBase } from '../utils/apiBase';
import { applyAuthToken } from '../utils/authHttp';

const AuthContext = createContext(null);

const API = getApiBase();
const AUTH_TIMEOUT_MS = 20000;
const SHOWCASE_MODE = process.env.REACT_APP_SHOWCASE_MODE === 'true';
const DEMO_EMAIL = 'demo@example.com';
const DEMO_PASSWORD = 'DemoPass123!';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const userRef = useRef(user);
  userRef.current = user;

  const logout = useCallback(() => {
    applyAuthToken(null);
    setToken(null);
    setUser(null);
  }, []);

  const hydrateCurrentUser = useCallback(async () => {
    const response = await axios.get(`${API}/auth/me`, { timeout: AUTH_TIMEOUT_MS });
    setUser(response.data);
    return response.data;
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      await hydrateCurrentUser();
    } catch (error) {
      console.error('Failed to fetch user', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [hydrateCurrentUser, logout]);

  useEffect(() => {
    if (token) {
      applyAuthToken(token);
      // Avoid depending on `user` here — it re-ran the effect every time user changed and could
      // interact badly with other providers. Ref reflects latest user after login/hydrate.
      if (userRef.current) {
        setLoading(false);
      } else {
        fetchUser();
      }
    } else if (SHOWCASE_MODE) {
      axios.post(`${API}/auth/login`, { email: DEMO_EMAIL, password: DEMO_PASSWORD }, { timeout: AUTH_TIMEOUT_MS })
        .then(async (response) => {
          const { access_token } = response.data;
          applyAuthToken(access_token);
          await hydrateCurrentUser();
          setToken(access_token);
        })
        .catch((err) => {
          console.warn('Showcase auto-login failed:', err?.message || err);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, fetchUser, hydrateCurrentUser]);

  const login = async (email, password, turnstileToken) => {
    const payload = { email, password };
    if (turnstileToken) {
      payload.turnstile_token = turnstileToken;
    }
    const response = await axios.post(`${API}/auth/login`, payload, { timeout: AUTH_TIMEOUT_MS });
    const { access_token } = response.data;
    applyAuthToken(access_token);
    try {
      const me = await axios.get(`${API}/auth/me`, { timeout: AUTH_TIMEOUT_MS });
      // Flush before returning so /login → navigate sees user (avoids bounce to /login)
      flushSync(() => {
        setUser(me.data);
        setToken(access_token);
      });
    } catch (error) {
      logout();
      throw error;
    }
  };

  const register = async (email, password, name, turnstileToken) => {
    const payload = { email, password, name };
    if (turnstileToken) {
      payload.turnstile_token = turnstileToken;
    }
    const response = await axios.post(`${API}/auth/register`, payload, { timeout: AUTH_TIMEOUT_MS });
    const { access_token } = response.data;
    applyAuthToken(access_token);
    try {
      const me = await axios.get(`${API}/auth/me`, { timeout: AUTH_TIMEOUT_MS });
      flushSync(() => {
        setUser(me.data);
        setToken(access_token);
      });
    } catch (error) {
      logout();
      throw error;
    }
  };

  const loginWithGoogleCredential = async (credential) => {
    const response = await axios.post(
      `${API}/auth/google`,
      { credential },
      { timeout: AUTH_TIMEOUT_MS }
    );
    const { access_token } = response.data;
    applyAuthToken(access_token);
    try {
      const me = await axios.get(`${API}/auth/me`, { timeout: AUTH_TIMEOUT_MS });
      flushSync(() => {
        setUser(me.data);
        setToken(access_token);
      });
    } catch (error) {
      logout();
      throw error;
    }
  };

  const authReady = Boolean(token) && !loading;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        authReady,
        login,
        register,
        loginWithGoogleCredential,
        logout,
        refreshUser: hydrateCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);